/**
 * UTM Service - Handles capturing, storing, and retrieving UTM parameters and referrer information
 */

import { getStoredConsent, isAnalyticsAllowed } from './cookieConsentService';
import { getStoredRegionInfo } from './regionService';
import { saveUserUtmAttribution } from './userService';

export interface UTMParameters {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_id?: string;
  utm_source_platform?: string;
  utm_creative_format?: string;
  utm_marketing_tactic?: string;
  utm_content?: string;
  utm_term?: string;
  exocode?: string;
  referrer?: string;
  landing_page?: string;
  timestamp?: number;
}

const UTM_STORAGE_KEY = 'kinkyfantasy_utm_params';
const UTM_SESSION_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

function isUtmStorageAllowed(): boolean {
  const consent = getStoredConsent();
  const region = getStoredRegionInfo();
  return isAnalyticsAllowed(region?.regionBucket, consent);
}

/**
 * Extract UTM parameters from URL search params
 */
export function extractUTMParameters(): UTMParameters {
  const params = new URLSearchParams(window.location.search);
  const utmParams: UTMParameters = {};

  // Extract standard UTM parameters
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');
  const utmId = params.get('utm_id');
  const utmSourcePlatform = params.get('utm_source_platform');
  const utmCreativeFormat = params.get('utm_creative_format');
  const utmMarketingTactic = params.get('utm_marketing_tactic');
  const utmContent = params.get('utm_content');
  const utmTerm = params.get('utm_term');
  const exocode = params.get('exocode');
  
  if (utmSource) utmParams.utm_source = utmSource;
  if (utmMedium) utmParams.utm_medium = utmMedium;
  if (utmCampaign) utmParams.utm_campaign = utmCampaign;
  if (utmId) utmParams.utm_id = utmId;
  if (utmSourcePlatform) utmParams.utm_source_platform = utmSourcePlatform;
  if (utmCreativeFormat) utmParams.utm_creative_format = utmCreativeFormat;
  if (utmMarketingTactic) utmParams.utm_marketing_tactic = utmMarketingTactic;
  if (utmContent) utmParams.utm_content = utmContent;
  if (utmTerm) utmParams.utm_term = utmTerm;
  if (exocode) utmParams.exocode = exocode;

  // Capture referrer if available
  if (document.referrer) {
    utmParams.referrer = document.referrer;
  }

  // Capture landing page
  utmParams.landing_page = window.location.href;
  
  // Add timestamp
  utmParams.timestamp = Date.now();

  return utmParams;
}

/**
 * Check if UTM parameters exist in the URL
 */
export function hasUTMParameters(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has('utm_source') || 
         params.has('utm_medium') || 
         params.has('utm_campaign') ||
         params.has('utm_id') ||
         params.has('utm_source_platform') ||
         params.has('utm_creative_format') ||
         params.has('utm_marketing_tactic') ||
         params.has('utm_content') ||
         params.has('utm_term') ||
         params.has('exocode');
}

/**
 * Save UTM parameters to localStorage
 */
export function saveUTMParameters(utmParams: UTMParameters): void {
  try {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams));
  } catch (error) {
    console.error('Failed to save UTM parameters to localStorage:', error);
  }
}

/**
 * Get stored UTM parameters from localStorage
 * Returns null if expired or not found
 */
export function getStoredUTMParameters(): UTMParameters | null {
  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const utmParams: UTMParameters = JSON.parse(stored);
    
    // Check if parameters are expired (older than 30 days)
    if (utmParams.timestamp && Date.now() - utmParams.timestamp > UTM_SESSION_DURATION) {
      clearUTMParameters();
      return null;
    }

    return utmParams;
  } catch (error) {
    console.error('Failed to retrieve UTM parameters from localStorage:', error);
    return null;
  }
}

/**
 * Clear stored UTM parameters
 */
export function clearUTMParameters(): void {
  try {
    localStorage.removeItem(UTM_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear UTM parameters:', error);
  }
}

/**
 * Get current UTM parameters - either from URL or from storage
 * Priority: URL parameters > Stored parameters
 */
export function getCurrentUTMParameters(): UTMParameters {
  if (!isUtmStorageAllowed()) {
    return {};
  }
  // First check if there are UTM parameters in the current URL
  if (hasUTMParameters()) {
    const utmParams = extractUTMParameters();
    saveUTMParameters(utmParams);
    return utmParams;
  }

  // No UTM params in URL - keep existing attribution if we already captured one
  const stored = getStoredUTMParameters();
  if (stored) {
    return stored;
  }

  // First visit with no UTM params and nothing stored yet - still capture
  // document.referrer/landing page so traffic without explicit tracking
  // params (e.g. plain affiliate links) isn't attributed as "unknown".
  const firstVisitParams = extractUTMParameters();
  saveUTMParameters(firstVisitParams);
  return firstVisitParams;
}

/**
 * Initialize UTM tracking - call this on app load
 * Returns the captured UTM parameters
 */
export function initializeUTMTracking(): UTMParameters {
  const currentParams = getCurrentUTMParameters();
  
  // If we have new UTM parameters in the URL, they're already saved by getCurrentUTMParameters
  // If not, we're using stored ones or empty object
  
  return currentParams;
}

/**
 * Persist the currently known UTM/referrer/exocode attribution to the user's
 * Firestore document, mirroring fbclidService's Firestore persistence so
 * traffic source survives beyond the 30-day localStorage window.
 */
export async function persistCurrentUtmAttributionToFirestore(
  userId?: string | null,
): Promise<void> {
  if (!isUtmStorageAllowed()) {
    return;
  }

  const resolvedUserId = userId?.trim();
  if (!resolvedUserId) {
    return;
  }

  const utmParams = getCurrentUTMParameters();
  if (Object.keys(utmParams).length === 0) {
    return;
  }

  try {
    await saveUserUtmAttribution(resolvedUserId, utmParams);
  } catch (error) {
    console.warn('Failed to persist UTM attribution to Firestore:', error);
  }
}

/**
 * Get UTM parameters formatted for analytics events
 * Removes timestamp and formats for Firebase Analytics
 */
export function getUTMParametersForAnalytics(): Record<string, string> {
  if (!isUtmStorageAllowed()) {
    return {};
  }
  const utmParams = getCurrentUTMParameters();
  const analyticsParams: Record<string, string> = {};

  // Convert to analytics-friendly format (remove timestamp, ensure string values)
  Object.entries(utmParams).forEach(([key, value]) => {
    if (key !== 'timestamp' && value !== undefined) {
      analyticsParams[key] = String(value);
    }
  });

  return analyticsParams;
}
